import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { TemplateRiskViewModel } from '../../../models/templateRiskViewModel';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Router } from '@angular/router';
import { UntypedFormBuilder } from '@angular/forms';
import { ConfirmationService } from 'src/app/modules/shared/services/confirmation.service';
import { AllowedFiltersService } from 'src/app/modules/shared/services/allowed-filters.service';
import { AuthenticationService } from 'src/app/modules/shared/services/authentication.service';
import { LocalisationService } from 'src/app/modules/shared/services/localisation.service';
import { RiskRAGMatrixService } from 'src/app/modules/shared/services/risk-rag-matrix.service';
import { AlertService } from 'src/app/modules/shared/services/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { WtValidators } from 'src/app/modules/shared/reactiveValidators/wtValidators';
import { AccountSettingsService } from 'src/app/modules/shared/services/account-settings.service';
import { TemplateRisksService } from '../../../services/tempalte-risks-service';
import { T } from 'src/assets/i18n/translation-keys';
import { FilterTypes } from 'src/app/modules/shared/enums/filterTypes';
import { RiskAddModalCoreComponent } from '../risk-add-modal-core';
import { RisksService } from '../../../services/risks.service';
import { RiskLikelihoods } from 'src/app/modules/shared/enums/risks/riskLikelihoods';
import { RiskImpact } from '../../../enums/riskImpact';

@Component({
    selector: 'app-template-risk-add-modal-component',
    templateUrl: './template-risk-add-modal-component.component.html',
    styleUrl: './template-risk-add-modal-component.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TemplateRiskAddModalComponentComponent extends RiskAddModalCoreComponent implements OnInit, OnDestroy {
  public risk: TemplateRiskViewModel = new TemplateRiskViewModel();
  public excludeLikelihoodFilterValues: RiskLikelihoods[] = [RiskLikelihoods.Issue];

  constructor(
    protected bsModalRef: BsModalRef,
    protected router: Router,
    protected fb: UntypedFormBuilder,
    protected changeDetectorRef: ChangeDetectorRef,
    protected confirmationService: ConfirmationService,
    protected allowedFiltersService: AllowedFiltersService,
    protected authenticationService: AuthenticationService,
    protected templateRiskService: TemplateRisksService,
    protected readonly localisationService: LocalisationService,
    protected readonly riskRAGMatrixService: RiskRAGMatrixService,
    protected readonly alertService: AlertService,
    protected readonly translateService: TranslateService,
    protected readonly wtValidators: WtValidators,
    protected readonly accountSettingsService: AccountSettingsService,
    protected riskService: RisksService,
  ){
    super(
      bsModalRef,
      router,
      fb,
      changeDetectorRef,
      confirmationService,
      allowedFiltersService,
      authenticationService,
      riskService,
      localisationService,
      riskRAGMatrixService,
      alertService,
      translateService,
      wtValidators,
      accountSettingsService
    )
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.templateRisk = this.translateService.instant(T.defaultLocalizations.template_risk.one);
    this.setLikelihoodFilter(this.likelihoodValue);
    this.setImpactFilter(this.impactValue);
  }

  setLikelihoodFilter(likelihood: RiskLikelihoods) {
    const filters = this.filters.filter((f) => f.filterType != FilterTypes.Risk_Likelihood);
    const newLikelihoodFilter = this.allowedFiltersService
      .getCachedAllowedFiltersByType(FilterTypes.Risk_Likelihood)
      .find((f) => f.filterValue == likelihood);
    this.filters.filter((x) => x.filterType == FilterTypes.Risk_Likelihood);
    this.filters = [...filters, newLikelihoodFilter];
  }

  setImpactFilter(impact: RiskImpact) {
    const filters = this.filters.filter((f) => f.filterType != FilterTypes.Risk_Impact);
    const newImpactFilter = this.allowedFiltersService
      .getCachedAllowedFiltersByType(FilterTypes.Risk_Impact)
      .find((f) => f.filterValue == impact);
    this.filters.filter((x) => x.filterType == FilterTypes.Risk_Impact);
    this.filters = [...filters, newImpactFilter];
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  public initAllowedFilters(): void {
    this.allowedFilters = this.allowedFiltersService.getCachedAllAllowedFilters();
  }

  public onSave() {
    this.risk.filters = this.filters;
    this.risk.title = this.form.controls.riskTitle.value.trim();
    this.risk.description = this.form.controls.riskDescription.value?.trim();
    this.risk.impact = this.getFilterValue(FilterTypes.Risk_Impact);
    this.risk.likelihood = this.getFilterValue(FilterTypes.Risk_Likelihood);
    this.risk.riskStrategyId = this.getFilterValue(FilterTypes.Risk_Strategy);
    this.risk.createdById = this.employee.id;
    this.risk.identifiedById = this.getFilterValue(FilterTypes.Identified_By);

    this.isLoading = true;

    this.subscriptions.add(
      this.templateRiskService.add(this.risk).subscribe({
        next: (result) => {
          this.isLoading = false;
          void this.alertService.success(
            this.translateService.instant(T.common.item_added, { item: this.templateRisk })
          );

          void this.router.navigate([`v2/risk/risk-assessments/template/${result.returnModel.id.toString()}`]);

          this.changeDetectorRef.detectChanges();
          this.closeModal();
        },
        error: (err) => {
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
        }
      })
    )
  }

  public validateState(): boolean {
    return this.form.get('riskTitle')?.valid
    && this.impactValue !== 0
    && this.likelihoodValue !== 0 as RiskLikelihoods;
  }

  protected handlePrefillForm(eventData: { item: any, notEditable: boolean }): void {
    super.handlePrefillForm(eventData);
  }
}
